import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import AppActions from '../../actions/app';
import AppStore from '../../stores/app';
import InviteStore from '../../stores/invite';
import BusinessStore from '../../stores/business';
import OrganizationStore from '../../stores/organization';
import AppStatusActions from '../../actions/appStatus';

import AccountSection from '@repo/ui/components/content/onboarding/AccountSection';
import WelcomeInfoSection from '@repo/ui/components/content/onboarding/WelcomeInfoSection';
import DoubleWide from '@repo/ui/components/content/onboarding/DoubleWide';
import { api as API } from '../../mixins/api';
import LocalStorage from '../../mixins/LocalStorage';
import Auth from '@/utils/Auth';
import {
  getNextLocation,
  getOnboardingType,
  getOnboardingDirect,
  getOnboardingReferral,
} from '../../utils/LocationState';

import { buildReturnPath } from '@repo/utils/URLFormatters';
import { handleSocialConnect, getSocialError } from '@/utils/SocialConnect.js';
import { withTranslation } from 'react-i18next';
import { PageContainer } from '@repo/ui/components/layout/PageLayouts';
import AlreadyLoggedIn from './AlreadyLoggedIn';
import MfaLoginModal from '../modals/MfaLoginModal';

class Login extends PureComponent {
  state = {
    requirePassword: false,
    loading: false,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  componentDidMount() {
    const { t } = this.props;
    AppActions.changePageTitle(t('common:nav.login'));
    // Hide loader status, since no XHR run on this page to do it automatically, see VolinspireAPI
    setTimeout(() => AppStatusActions.setIsXhrActive(false), 30);
  }

  handleLogin = ({ email, password }, handleServerResponse) => {
    const { invite } = InviteStore.getState();
    const { location } = this.props;
    this.setState({ loading: true });
    if (email && !password && !this.state.requirePassword) {
      const next = getNextLocation(invite, location, 'login');
      const returnPath = buildReturnPath(next.pathname, next.query);
      API.makeRequest(`/sso/tenants?email=${encodeURIComponent(email)}`, 'GET', {}, (err, res) => {
        this.setState({ loading: false });
        if (err || !res || !res.body) {
          this.setState({ requirePassword: true });
        } else {
          let t = res.body.tenants || [];
          if (!t.length || res.body.usePasswordLogin) {
            return this.setState({ requirePassword: true });
          }
          const sso = t[0];
          LocalStorage.setItem('registration.method', 'sso');
          LocalStorage.setItem('registration.email', email);
          // Wait a sec to save localStorage and display loading indicators (if any)
          setTimeout(
            () =>
              (window.location = `${import.meta.env.VITE_AUTH_HOST}/redirect/${
                sso.type
              }?email=${encodeURIComponent(email)}&returnPath=${returnPath}`),
            20,
          );
        }
      });
    } else {
      Auth.login(email, password, (loggedIn, accessToken, err) => {
        if (loggedIn) {
          LocalStorage.setItem('registration.method', 'email');
          LocalStorage.setItem('registration.email', email);

          // Setting these values for instant login effect without reload
          API.activeAccessToken = accessToken;
          API.accessToken = accessToken;
          // Fetch the user profile since we're not loading the next page from server
          API.makeRequest(`/user`, 'GET', {}, (_, res) => {
            AppActions.login(res?.body);
            // Provide enough time for stores to update
            setTimeout(() => {
              this.props.router.push(getNextLocation(invite, location, 'login'));
            }, 10);
          });
        } else {
          this.setState({ loading: false });
          if (err?.message?.includes('RateLimitExceededError')) {
            handleServerResponse(() => true)(
              new Error('Too many attempts, please wait and try again'),
            );
          } else if (
            err?.message?.includes('invalid_grant') ||
            err?.message?.includes('invalid_credentials') ||
            err?.message?.includes('access_denied')
          ) {
            handleServerResponse(() => true)(new Error('Incorrect email or password'));
          } else if (
            err?.message?.includes('insufficient_scope') ||
            err?.message?.includes('MFA required')
          ) {
            const mfaType = err.message?.toLowerCase().includes('email') ? 'email' : 'totp';
            AppActions.setModal({
              component: MfaLoginModal,
              options: {
                mfaType,
              },
            });
            handleServerResponse(() => true)(
              new Error(
                mfaType === 'email'
                  ? `Your account has multi-factor authentication enabled, please check your email to continue.`
                  : `Unsupported TOTP Authentication`,
              ),
            );
          } else {
            handleServerResponse(() => true)(
              new Error('An error occurred, please wait and try again'),
            );
          }
        }
      });
    }
  };

  render() {
    const { user } = AppStore.getState();
    const { location, t } = this.props;
    const { requirePassword, loading } = this.state;
    const { error } = location.query;
    const { invite } = InviteStore.getState();
    const { business } = BusinessStore.getState();
    const { organization } = OrganizationStore.getState();

    const context =
      business && business.slug
        ? { ...business, type: 'business' }
        : { ...organization, type: 'organization' };

    const type = getOnboardingType(invite, location);
    const direct = getOnboardingDirect(location, context);
    const referral = getOnboardingReferral(location);
    const socialError = getSocialError(error, t);

    const lastLogin = {
      method: LocalStorage.getItem('registration.method'),
      email: LocalStorage.getItem('registration.email'),
    };

    return (
      <PageContainer>
        {user ? (
          <AlreadyLoggedIn user={user} socialError={socialError} />
        ) : (
          <DoubleWide>
            <WelcomeInfoSection type={type} invite={invite} direct={direct} referral={referral} />
            <AccountSection
              api={API}
              socialError={socialError}
              token={location.query.token}
              state={location.state}
              create={false}
              lastLogin={lastLogin}
              loading={loading}
              nextLocation={getNextLocation(invite, location, 'login')}
              onEmailLogin={this.handleLogin}
              onSocialConnect={handleSocialConnect}
              onBack={() => this.setState({ requirePassword: false })}
              hidePasswordField={!requirePassword}
              allowDisableEmailField
              showApple
            />
          </DoubleWide>
        )}
      </PageContainer>
    );
  }
}

export default withTranslation(['common', 'commonPlat'])(Login);
